import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import { Redirect } from "react-router";
import { LocaleValue } from "@eres/core/dist/LocaleValue";
import { LocaleUtil } from "@eres/core";
import ProcedureDocuments from "./ProcedureDocuments";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { getParticipantID } from "@10x/sso";
import _ from "lodash";
import RTRegSuccessPopup from "@my-dld/components/RTRegistration/RTRegSuccessPopup";
import { JQModal } from "@my-dld/components/Popup/Utils";
import ProcedureDetails from "./ProcedureDetails";
import RoleWithParties from "./RoleWithParties";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { RTRegistrationContext } from "@my-dld/contexts/RTRegistrationContext";
import { extractPropInfo } from "../MyDLD/PropertyDetails";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { Party, ProcedureDoc, ProcedureTransaction, Role } from "@eres/rtregistrationapi/entities";
import { RTRegistrationApi } from "@eres/rtregistrationapi";
import PageContent from "@my-dld/components/PageContent";

interface IRTRegistrationTrack {
	index: number;
	name: LocaleValue;
	screen: RTRegistrationComponent<any, any>;
}

type RTRegistrationComponent<P, S> = React.ComponentClass<P, S>

export enum TrackIndex {
	PROCEDURE_DETAILS = 0,
	PARTY_INFO = 1,
	PROCEDURE_DOCUMENTS = 2,
}

const RTRegistrationTrack: IRTRegistrationTrack[] = [
	{
		index: TrackIndex.PROCEDURE_DETAILS,
		name: LVStrings.rt_procedure_details,
		screen: ProcedureDetails,
	},
	{
		index: TrackIndex.PARTY_INFO,
		name: LVStrings.rt_parties_information,
		screen: RoleWithParties,
	},
	{
		index: TrackIndex.PROCEDURE_DOCUMENTS,
		name: LVStrings.rt_upload_documents_and_submit,
		screen: ProcedureDocuments,
	}
];

interface Props extends RoutedViewProps {

}

interface State {
	procedure: ProcedureTransaction;
	current: IRTRegistrationTrack;
	showPopup?: boolean;
	procedureNumber?: string;
}

class RTRegistration extends Component<Props, State> {
	static contextType = RTRegistrationContext;
	context!: React.ContextType<typeof RTRegistrationContext>;

	constructor( props: Props ) {
		super( props );
		const locState = props.location.state;
		this.state = {
			current: RTRegistrationTrack[ 0 ],
			procedure: ( locState && locState.procedure ) ? locState.procedure : undefined,
		};
	}

	private static trackEvent( isSuccess: boolean, procedure: ProcedureTransaction, pNumber?: string ) {
		const action = isSuccess ? "SubmitProcedureSuccess" : "SubmitProcedureFail";
		trackEventWithData( "RTRegistration", action, {
			procedureId: "" + procedure.procedureId,
			tabuPropertyId: "" + procedure.propertyId,
			rtOfficeParticipantId: "" + procedure.RTOfficeParticipantId,
			procedureNumber: pNumber ? pNumber : "",
		} );
	}

	componentWillUnmount() {
		JQModal( "AddPartyPopup" ).hide();
		JQModal( "AddPartyOnlyNamePopup" ).hide();
		JQModal( "RTRegSuccessPopup" ).hide();
	}

	render() {
		const { current, procedure, showPopup, procedureNumber } = this.state;
		const property = this.getPropertyFromContext();
		if ( procedure == null || property == null )
			return <Redirect to={ "/mydld" }/>;
		const { val, lbl } = extractPropInfo( property );
		return <PageContent
			title={ `${ Strings.rt_register_transaction_at_rt_office } - ${ lbl } : ${ val }` }
			hideQLinks={ true }>
			<PageTitle title={ Strings.rt_register_transaction_at_rt_office } paths={ [ DashboardBCItem ] }/>
			<div className="row">
				<div className="online-registration-detail">
					<div className="col-xl-8 col-lg-8 col-md-8 col-sm-12 col-12 online-registration-form">
						{ current && <this.state.current.screen
							holder={ this }
							procedure={ procedure }
							onPrevious={ this.onPrevious }
							onNext={ this.onNext }/> }
					</div>
					<div className="col-xl-4 col-lg-4 col-md-4 col-sm-12 col-12 side-panel">
						<ul className="list-group rt-list-steps-link" id="TR-list-tab" role="tablist">
							<li className="list-group-item disabled">{ Strings.rt_register_transaction_at_rt_office }</li>
							{ RTRegistrationTrack.map( ( item: IRTRegistrationTrack ) => {
								const active = ( current.index === item.index ) ? " active" : "";
								return <li
									key={ "RTRegistrationTrack_" + item.index }
									className={ "list-group-item list-group-item-action" + active }>
									<a
										data-bs-toggle="list"
										role="tab">{ Strings.step + " " + ( item.index + 1 ) + " - " + LocaleUtil.getLocalValue( item.name ) }</a>
								</li>;
							} )
							}
						</ul>
					</div>
				</div>
			</div>
			{ showPopup && <RTRegSuccessPopup
				procedureNumber={ procedureNumber }
				onDismiss={ this.onSuccessDismiss }/> }
		</PageContent>;
	}

	private onPrevious = ( index: TrackIndex ) => {
		if ( index > 0 ) {
			this.setState( { current: RTRegistrationTrack[ index - 1 ] } );
		}
	};

	private onNext = ( index: TrackIndex ) => {
		if ( index < ( RTRegistrationTrack.length - 1 ) ) {
			this.setState( { current: RTRegistrationTrack[ index + 1 ] } );
		} else {
			this.uploadProcedureDocuments();
		}
	};

	private uploadProcedureDocuments() {
		const { procedure } = this.state;
		showLoading();
		RTRegistrationApi.uploadDocuments( procedure.Documents ).then( ( docs ) => {
			procedure.Documents = docs;
			this.submitProcedure( procedure );
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason );
		} );
	}

	private submitProcedure( procedure: ProcedureTransaction ) {
		const cProcedure: ProcedureTransaction = _.cloneDeep( procedure );
		this.removeLocalDoc( cProcedure.Documents );
		cProcedure.Roles.forEach( ( role: Role ) => {
			role.Parties.forEach( ( party: Party ) => {
				if ( party && party.Documents && party.Documents.length > 0 ) {
					this.removeLocalDoc( party.Documents );
				}
			} );
		} );
		RTRegistrationApi.saveProcedureTransaction( getParticipantID()!, cProcedure ).then( ( resp: any ) => {
			hideLoading();
			if ( resp ) {
				if ( resp.ValidationErrorsList && resp.ValidationErrorsList.length > 0 ) {
					RTRegistration.trackEvent( false, cProcedure );
					showErrorMessage( RTRegistrationApi.getValidationError( resp.ValidationErrorsList ) );
				} else {
					const pNumber = "" + resp.ProcedureNumber;
					RTRegistration.trackEvent( true, cProcedure, pNumber );
					this.setState( {
						showPopup: true,
						procedureNumber: pNumber,
					}, () => {
						JQModal( "RTRegSuccessPopup" ).show();
					} );
				}
			}
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason );
		} );
	}

	private removeLocalDoc( docs: ProcedureDoc[] ) {
		if ( docs && docs.length > 0 ) {
			docs.forEach( ( doc: ProcedureDoc ) => {
				doc.doc = undefined;
			} );
		}
	}

	private onSuccessDismiss = () => {
		this.props.history.goBack();
	};

	private getPropertyFromContext(): AssetCard | undefined {
		if ( this.context ) {
			const state: any = this.context.getData( "ProcedureSelection" );
			if ( state && state.hasOwnProperty( "property" ) ) {
				return state.property;
			}
		}
		return undefined;
	}
}

export interface RTRegistrationProps {
	holder: any;
	procedure: ProcedureTransaction;
	onPrevious: ( index: TrackIndex ) => void;
	onNext: ( index: TrackIndex ) => void;
}

export default RTRegistration;
